import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Plane, Clock, Moon, Cloud, Navigation, Gauge, 
  Cog, Wrench, Settings2, Shield, Award, CheckCircle2,
  Layers, RotateCcw, Timer
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PilotExperience, MechanicExperience, JobCategory } from '@/types/jobWizard';
import { motion } from 'framer-motion';

interface JobExperienceDetailSectionProps {
  category: JobCategory;
  pilotExperience?: PilotExperience;
  mechanicExperience?: MechanicExperience;
  experienceSummary?: string;
  keyAchievements?: string;
}

// Animated progress bar component
const AnimatedProgress: React.FC<{ value: number; max: number; label: string; icon: React.ReactNode; gradient: string }> = ({
  value,
  max,
  label,
  icon,
  gradient,
}) => {
  const percentage = Math.min((value / max) * 100, 100);
  
  return (
    <motion.div 
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-1.5"
    >
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          {icon}
          <span>{label}</span>
        </div>
        <span className="font-semibold tabular-nums">
          {value?.toLocaleString() || 0}
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={cn("h-full rounded-full", gradient)}
        />
      </div>
    </motion.div>
  );
};

// Experience badge with icon
const ExperienceBadge: React.FC<{ 
  label: string; 
  active: boolean; 
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning';
}> = ({ label, active, icon, variant = 'default' }) => {
  if (!active) return null;
  
  const variantClasses = {
    default: 'bg-primary/10 text-primary border-primary/20',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700/40',
    warning: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700/40',
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <Badge className={cn("gap-1.5 border", variantClasses[variant])}>
        {icon}
        {label}
      </Badge>
    </motion.div>
  );
};

const JobExperienceDetailSection: React.FC<JobExperienceDetailSectionProps> = ({
  category,
  pilotExperience,
  mechanicExperience,
  experienceSummary,
  keyAchievements,
}) => {
  // Pilot Experience Display
  if (category === 'pilot' && pilotExperience) {
    const exp = pilotExperience;
    const maxHours = Math.max(
      exp.totalFlightHours || 0,
      15000 // Reference max for visualization
    );
    
    return (
      <div className="space-y-6">
        {/* Flight Hours Breakdown */}
        <Card className="border-blue-200/60 dark:border-blue-700/40 bg-gradient-to-br from-blue-50/50 to-sky-50/30 dark:from-blue-900/20 dark:to-sky-900/10 overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Plane className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              Flight Experience
              {exp.totalFlightHours && (
                <Badge className="ml-auto bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                  {exp.totalFlightHours.toLocaleString()} Total Hours
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              {exp.picHours && (
                <AnimatedProgress 
                  value={exp.picHours} 
                  max={maxHours}
                  label="PIC (Pilot in Command)"
                  icon={<Shield className="w-4 h-4" />}
                  gradient="bg-gradient-to-r from-blue-500 to-blue-600"
                />
              )}
              {exp.sicHours && (
                <AnimatedProgress 
                  value={exp.sicHours} 
                  max={maxHours}
                  label="SIC (Second in Command)"
                  icon={<Layers className="w-4 h-4" />}
                  gradient="bg-gradient-to-r from-sky-500 to-sky-600"
                />
              )}
              {exp.helicopterPicHours && (
                <AnimatedProgress 
                  value={exp.helicopterPicHours} 
                  max={maxHours}
                  label="Helicopter PIC"
                  icon={<RotateCcw className="w-4 h-4" />}
                  gradient="bg-gradient-to-r from-violet-500 to-violet-600"
                />
              )}
              {exp.turbineHours && (
                <AnimatedProgress 
                  value={exp.turbineHours} 
                  max={maxHours}
                  label="Turbine Time"
                  icon={<Gauge className="w-4 h-4" />}
                  gradient="bg-gradient-to-r from-indigo-500 to-indigo-600"
                />
              )}
              {exp.multiEngineHours && (
                <AnimatedProgress 
                  value={exp.multiEngineHours} 
                  max={maxHours}
                  label="Multi-Engine"
                  icon={<Cog className="w-4 h-4" />}
                  gradient="bg-gradient-to-r from-purple-500 to-purple-600"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Special Conditions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Cloud className="w-4 h-4 text-primary" />
              Special Conditions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {exp.nightHours && (
                <div className="p-3 rounded-xl bg-slate-900 text-white dark:bg-slate-800">
                  <div className="flex items-center gap-2 mb-1">
                    <Moon className="w-4 h-4 text-yellow-400" />
                    <span className="text-xs opacity-80">Night Flying</span>
                  </div>
                  <div className="text-lg font-bold">{exp.nightHours.toLocaleString()} hrs</div>
                </div>
              )}
              {exp.ifrHours && (
                <div className="p-3 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
                  <div className="flex items-center gap-2 mb-1">
                    <Cloud className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-xs text-muted-foreground">IFR</span>
                  </div>
                  <div className="text-lg font-bold">{exp.ifrHours.toLocaleString()} hrs</div>
                </div>
              )}
              {exp.crossCountryHours && (
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/30 dark:to-green-900/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Navigation className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-xs text-emerald-700 dark:text-emerald-300">Cross-Country</span>
                  </div>
                  <div className="text-lg font-bold text-emerald-800 dark:text-emerald-200">{exp.crossCountryHours.toLocaleString()} hrs</div>
                </div>
              )}
              {exp.instructorHours && (
                <div className="p-3 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs text-amber-700 dark:text-amber-300">Instructor</span>
                  </div>
                  <div className="text-lg font-bold text-amber-800 dark:text-amber-200">{exp.instructorHours.toLocaleString()} hrs</div>
                </div>
              )}
              {exp.last90DaysHours && (
                <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-50 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Timer className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                    <span className="text-xs text-cyan-700 dark:text-cyan-300">Last 90 Days</span>
                  </div>
                  <div className="text-lg font-bold text-cyan-800 dark:text-cyan-200">{exp.last90DaysHours} hrs</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Mechanic Experience Display
  if (category === 'mechanic' && mechanicExperience) {
    const exp = mechanicExperience;
    
    return (
      <div className="space-y-6">
        {/* Experience Overview */}
        <Card className="border-orange-200/60 dark:border-orange-700/40 bg-gradient-to-br from-orange-50/50 to-amber-50/30 dark:from-orange-900/20 dark:to-amber-900/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              Maintenance Experience
              {exp.totalExperienceYears && (
                <Badge className="ml-auto bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                  {exp.totalExperienceYears}+ Years
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Specialties */}
            {(exp.primarySpecialty || exp.secondarySpecialty) && (
              <div className="mb-4 p-3 bg-background rounded-lg border border-border">
                <p className="text-xs text-muted-foreground mb-2">Specializations</p>
                <div className="flex flex-wrap gap-2">
                  {exp.primarySpecialty && (
                    <Badge className="bg-orange-500 text-white">
                      Primary: {exp.primarySpecialty}
                    </Badge>
                  )}
                  {exp.secondarySpecialty && (
                    <Badge variant="outline">{exp.secondarySpecialty}</Badge>
                  )}
                </div>
              </div>
            )}

            {/* Engine & Aircraft Experience - Only show selected types */}
            {(exp.turbineEngineExperience || exp.pistonEngineExperience || exp.helicopterExperience || exp.fixedWingExperience) && (
              <div className="flex flex-wrap gap-2 mb-4">
                {exp.turbineEngineExperience && (
                  <ExperienceBadge 
                    label="Turbine Engine" 
                    active={true} 
                    icon={<Gauge className="w-3.5 h-3.5" />}
                    variant="success"
                  />
                )}
                {exp.pistonEngineExperience && (
                  <ExperienceBadge 
                    label="Piston Engine" 
                    active={true} 
                    icon={<Settings2 className="w-3.5 h-3.5" />}
                    variant="success"
                  />
                )}
                {exp.helicopterExperience && (
                  <ExperienceBadge 
                    label="Helicopter" 
                    active={true} 
                    icon={<RotateCcw className="w-3.5 h-3.5" />}
                    variant="default"
                  />
                )}
                {exp.fixedWingExperience && (
                  <ExperienceBadge 
                    label="Fixed Wing" 
                    active={true} 
                    icon={<Plane className="w-3.5 h-3.5" />}
                    variant="default"
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Maintenance Capabilities */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-primary" />
              Maintenance Capabilities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <ExperienceBadge 
                label="Heavy Check" 
                active={!!exp.heavyCheckExperience} 
                icon={<Wrench className="w-3 h-3" />}
                variant="success"
              />
              <ExperienceBadge 
                label="Line Maintenance" 
                active={!!exp.lineMaintenance} 
                icon={<Clock className="w-3 h-3" />}
                variant="success"
              />
              <ExperienceBadge 
                label="Shop Maintenance" 
                active={!!exp.shopMaintenance} 
                icon={<Settings2 className="w-3 h-3" />}
                variant="success"
              />
              <ExperienceBadge 
                label="Avionics" 
                active={!!exp.avionicsExperience} 
                icon={<Gauge className="w-3 h-3" />}
                variant="default"
              />
              <ExperienceBadge 
                label="Composite Repair" 
                active={!!exp.compositeRepair} 
                icon={<Layers className="w-3 h-3" />}
                variant="default"
              />
              <ExperienceBadge 
                label="NDT Certified" 
                active={!!exp.ndtCertified} 
                icon={<Award className="w-3 h-3" />}
                variant="warning"
              />
            </div>

            {/* Major Work Stats */}
            {(exp.majorRepairsCount || exp.majorAlterationsCount) && (
              <div className="grid grid-cols-2 gap-3 mt-4">
                {exp.majorRepairsCount && (
                  <div className="p-3 bg-muted/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary">{exp.majorRepairsCount}</div>
                    <div className="text-xs text-muted-foreground">Major Repairs</div>
                  </div>
                )}
                {exp.majorAlterationsCount && (
                  <div className="p-3 bg-muted/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary">{exp.majorAlterationsCount}</div>
                    <div className="text-xs text-muted-foreground">Major Alterations</div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // General Experience (for other categories)
  if (experienceSummary || keyAchievements) {
    return (
      <div className="space-y-6">
        {experienceSummary && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Experience Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {experienceSummary}
              </p>
            </CardContent>
          </Card>
        )}
        
        {keyAchievements && (
          <Card className="border-amber-200/60 dark:border-amber-700/40 bg-gradient-to-br from-amber-50/50 to-yellow-50/30 dark:from-amber-900/20 dark:to-yellow-900/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                Key Achievements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {keyAchievements}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return null;
};

export default JobExperienceDetailSection;
